'use client';

/**
 * 상세 다이얼로그 아래 「비슷한 소재」 — 이미지가 비슷한 광고 소재 · 이미지가 비슷한 소셜 게시물.
 *
 * - 광고 소재: 광고 화면 유사 소재와 같은 창구(`/api/meta-ads/similar`, CLIP 티어 켬) — 광고는 광고 id, 게시물은 썸네일 주소.
 *   ⚠️ **소셜 글만 다루는 화면에서는 끈다**(`opts.ads === false`) — 보기만 바꾼 자리에 다른 원장이 섞이면 안 된다.
 * - 소셜 게시물: `/api/search-all/similar-posts` — 게시물은 id, 광고는 소재 이미지 주소. 받은 id 를 `feed.byIds` 로 원본을 채워 카드로 만든다.
 * ⚠️ 게시물 이미지 색인은 최근 게시물 · SNS 다섯 채널뿐이다 — 그 밖의 카드는 게시물 줄이 비어 있다.
 * 다이얼로그가 연 카드에서만 묻고, 받은 결과는 캐시로 다시 묻지 않는다.
 */

import type { SimilarAdItem } from 'src/actions/meta-ads/meta-ads-similar';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getIngestedPosts } from 'src/utils/moments/domain/moment-ingest';

import { searchAllKeys } from 'src/query-keys/search-all';
import { fetchMomentFeedByIds } from 'src/actions/els/moment';
import { fetchSimilarAds } from 'src/actions/meta-ads/meta-ads-similar';
import { fetchSimilarPosts } from 'src/actions/search-all-similar-posts';

import { dedupeCards, toOrganicMediaCard } from '../../../utils/search-all-media-cards';
import {
  similarAdsKey,
  similarAdsQuery,
  similarPostsKey,
  canAskSimilarAds,
  toSimilarSubject,
  similarPostsQuery,
  canAskSimilarPosts,
} from '../../../utils/search-all-similar-subject';

import type { MediaCard } from '../../../utils/search-all-media-cards';
import type { SimilarSubject } from '../../../utils/search-all-similar-subject';

const STALE_MS = 10 * 60 * 1000;
/** 한 줄에 펴는 수 */
const SIMILAR_LIMIT = 12;
/** 재료가 없을 때 「물을 수 있나」에 넘기는 빈 값 — 늘 `false` 가 나온다 */
const EMPTY_SUBJECT: SimilarSubject = { adId: '', postId: '', imageUrl: '' };


function text(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/**
 * @param card 열린 카드 — 없으면 묻지 않는다
 * @param opts `ads: false` 면 **광고 소재를 아예 안 묻는다** — 소셜 글만 다루는 화면에서 쓴다
 *   (브랜드 멘션 게시글 탭의 섬네일 보기). 보기만 바꾼 자리에 다른 원장이 섞이면
 *   「보기를 바꿨는데 내용이 달라졌다」가 된다.
 */
export function useSearchAllSimilar(card: MediaCard | null, opts?: { ads?: boolean }) {
  const wantAds = opts?.ads !== false;
  /** 무엇으로 물을지 — 재료 고르기는 `search-all-similar-subject` 가 정본이다(격자의 「비슷한 소재」와 같은 재료) */
  const subject = useMemo(() => toSimilarSubject(card), [card]);
  const postId = subject?.postId ?? '';
  const adKey = subject ? similarAdsKey(subject) : '';
  const postKey = subject ? similarPostsKey(subject) : '';

  const adsQuery = useQuery({
    queryKey: searchAllKeys.similarAds(adKey),
    enabled: wantAds && Boolean(subject) && canAskSimilarAds(subject ?? EMPTY_SUBJECT),
    staleTime: STALE_MS,
    queryFn: async ({ signal }): Promise<SimilarAdItem[]> => {
      const res = await fetchSimilarAds(similarAdsQuery(subject!, SIMILAR_LIMIT), signal);
      return (res.similar ?? []).filter((s) => s.previewImage && s.adArchiveId !== subject!.adId);
    },
  });

  const postsQuery = useQuery({
    queryKey: searchAllKeys.similarPosts(postKey),
    enabled: Boolean(subject) && canAskSimilarPosts(subject ?? EMPTY_SUBJECT),
    staleTime: STALE_MS,
    queryFn: async ({ signal }) => {
      const similar = await fetchSimilarPosts(similarPostsQuery(subject!, SIMILAR_LIMIT + 1), signal);
      const ids = similar.map((s) => s.id).filter((id) => id !== postId);
      if (ids.length === 0) return [];
      const { posts } = await fetchMomentFeedByIds({ ids, sourceFieldSet: 'allButSearchPost' });
      const ingested = (await getIngestedPosts(posts)) as Record<string, unknown>[];
      // 백엔드가 준 비슷한 순서를 지킨다 — byIds 는 순서를 보장하지 않는다
      const order = new Map(ids.map((id, i) => [id, i]));
      return ingested
        .filter((p) => order.has(text(p.id)))
        .sort((a, b) => (order.get(text(a.id)) ?? 0) - (order.get(text(b.id)) ?? 0));
    },
  });

  const posts = useMemo<MediaCard[]>(
    () => dedupeCards((postsQuery.data ?? []).map(toOrganicMediaCard)).filter((c) => c.key !== card?.key).slice(0, SIMILAR_LIMIT),
    [postsQuery.data, card?.key],
  );

  return {
    ads: wantAds ? adsQuery.data ?? [] : [],
    adsLoading: wantAds && adsQuery.isFetching && !adsQuery.data,
    posts,
    postsLoading: postsQuery.isFetching && !postsQuery.data,
  };
}
